<template>
  <ion-page>
    <ion-header class="header-container">
      <ion-toolbar class="header-toolbar">
        <ion-buttons slot="start">
          <ion-button @click="goBack" fill="clear" class="back-button">
            <ion-icon
              slot="icon-only"
              :icon="chevronBack"
              class="back-icon"
            ></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-title class="page-title-header"
          >Business Hours Management</ion-title
        >
      </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding">
      <div class="container">
        <div v-if="loading" class="loading-message">
          <ion-spinner></ion-spinner>
          <p>Loading business hours...</p>
        </div>

        <div v-else class="hours-form">
          <div class="form-header">
            <p class="instructions">
              Set your cafe's operating hours for each day of the week. Leave
              unchecked for days when closed.
            </p>
          </div>

          <div v-for="(day, index) in daysOfWeek" :key="index" class="day-row">
            <div class="day-checkbox">
              <ion-checkbox
                v-model="hours[index].isOpen"
                @ionChange="toggleDay(index)"
              ></ion-checkbox>
              <label class="day-name">{{ day }}</label>
            </div>

            <div v-if="hours[index].isOpen" class="time-inputs">
              <div class="time-input-group">
                <label>Open:</label>
                <input
                  type="time"
                  v-model="hours[index].openTime"
                  class="time-input"
                />
              </div>

              <div class="time-input-group">
                <label>Close:</label>
                <input
                  type="time"
                  v-model="hours[index].closeTime"
                  class="time-input"
                />
              </div>
            </div>

            <div v-else class="closed-label">Closed</div>
          </div>

          <div class="action-buttons">
            <ion-button
              @click="saveBusinessHours"
              :disabled="saving"
              expand="block"
              color="primary"
            >
              <ion-spinner v-if="saving" name="crescent"></ion-spinner>
              <span v-else>Save Business Hours</span>
            </ion-button>
          </div>

          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>

          <div v-if="successMessage" class="success-message">
            {{ successMessage }}
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { defineComponent } from "vue";
import { chevronBack } from "ionicons/icons";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonIcon,
  IonTitle,
  IonContent,
  IonCheckbox,
  IonSpinner,
} from "@ionic/vue";
import axios from "axios";

const BASE_URI =
  process.env.VUE_APP_BASE_URI !== undefined
    ? process.env.VUE_APP_BASE_URI
    : "https://api.onecoffeeapp.com.au";

export default defineComponent({
  name: "AdminBusinessHours",
  components: {
    IonPage,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonTitle,
    IonContent,
    IonCheckbox,
    IonSpinner,
  },
  setup() {
    return {
      chevronBack,
    };
  },
  data() {
    return {
      daysOfWeek: [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      hours: [
        { day: 0, isOpen: false, openTime: "09:00", closeTime: "17:00" },
        { day: 1, isOpen: false, openTime: "09:00", closeTime: "17:00" },
        { day: 2, isOpen: false, openTime: "09:00", closeTime: "17:00" },
        { day: 3, isOpen: false, openTime: "09:00", closeTime: "17:00" },
        { day: 4, isOpen: false, openTime: "09:00", closeTime: "17:00" },
        { day: 5, isOpen: false, openTime: "09:00", closeTime: "17:00" },
        { day: 6, isOpen: false, openTime: "09:00", closeTime: "17:00" },
      ],
      shopId: null,
      loading: true,
      saving: false,
      errorMessage: "",
      successMessage: "",
    };
  },
  methods: {
    goBack() {
      this.$router.push("/admin");
    },
    toggleDay(index) {
      // When day is toggled, reset times to default if opening
      if (this.hours[index].isOpen) {
        this.hours[index].openTime = "09:00";
        this.hours[index].closeTime = "17:00";
      }
    },
    async loadBusinessHours() {
      this.loading = true;
      this.errorMessage = "";

      try {
        const cafeId = localStorage.getItem("cafeId");

        if (!cafeId) {
          this.errorMessage = "No cafe associated with this account.";
          this.loading = false;
          return;
        }

        // Get shop integer ID from cafeId (uuid)
        const shopResponse = await axios.get(
          `${BASE_URI}/cafe/get-coffee-shops/${cafeId}`,
          {
            headers: {
              Authorization: localStorage.getItem("token"),
              "Content-Type": "application/json;charset=UTF-8",
            },
          }
        );

        if (!shopResponse.data.data || !shopResponse.data.data.length) {
          this.errorMessage = "Cafe not found.";
          this.loading = false;
          return;
        }

        this.shopId = shopResponse.data.data[0].id;

        // Load business hours
        const hoursResponse = await axios.get(
          `${BASE_URI}/admin/business-hours/${this.shopId}`,
          {
            headers: {
              Authorization: localStorage.getItem("token"),
              "Content-Type": "application/json;charset=UTF-8",
            },
          }
        );

        if (hoursResponse.data.data && hoursResponse.data.data.length > 0) {
          // Populate hours from database
          hoursResponse.data.data.forEach((dbHour) => {
            // Validate day index and time format
            if (
              !this.hours[dbHour.day] ||
              typeof dbHour.openTime !== "string" ||
              typeof dbHour.closeTime !== "string" ||
              dbHour.openTime.length < 5 ||
              dbHour.closeTime.length < 5
            ) {
              console.warn("Invalid business hours data:", dbHour);
              return;
            }

            this.hours[dbHour.day].isOpen = true;
            // Remove seconds from time format (HH:MM:SS -> HH:MM)
            this.hours[dbHour.day].openTime = dbHour.openTime.substring(0, 5);
            this.hours[dbHour.day].closeTime = dbHour.closeTime.substring(0, 5);
          });
        }

        this.loading = false;
      } catch (error) {
        console.error("Error loading business hours:", error);
        this.errorMessage = "Failed to load business hours. Please try again.";
        this.loading = false;
      }
    },
    async saveBusinessHours() {
      this.saving = true;
      this.errorMessage = "";
      this.successMessage = "";

      try {
        // Prepare hours data (only include open days)
        const hoursToSave = this.hours
          .filter((h) => h.isOpen)
          .map((h) => ({
            day: h.day,
            openTime: h.openTime,
            closeTime: h.closeTime,
          }));

        await axios.post(
          `${BASE_URI}/admin/business-hours`,
          {
            shopId: this.shopId,
            hours: hoursToSave,
          },
          {
            headers: {
              Authorization: localStorage.getItem("token"),
              "Content-Type": "application/json;charset=UTF-8",
            },
          }
        );

        this.successMessage = "Business hours saved successfully!";
        this.saving = false;

        // Clear success message after 3 seconds
        setTimeout(() => {
          this.successMessage = "";
        }, 3000);
      } catch (error) {
        console.error("Error saving business hours:", error);
        this.errorMessage =
          error.response?.data?.error ||
          "Failed to save business hours. Please try again.";
        this.saving = false;
      }
    },
  },
  async mounted() {
    await this.loadBusinessHours();
  },
});
</script>

<style scoped>
/* Header Styles */
.header-container {
  --background: #f87e63;
}

.header-toolbar {
  --background: #f87e63;
  --border-width: 0;
  --padding-top: 8px;
  --padding-bottom: 8px;
  --padding-start: 16px;
  --padding-end: 16px;
  --min-height: 64px;
}

.page-title-header {
  --color: #ffffff;
  font-family: "Gilroy", sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
}

.back-button {
  --padding-start: 0;
  --padding-end: 0;
  margin: 0;
}

.back-icon {
  color: #ffffff;
  font-size: 24px;
}

.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.loading-message {
  text-align: center;
  padding: 40px 20px;
}

.loading-message p {
  margin-top: 10px;
  font-family: "Gilroy";
  color: #828282;
}

.form-header {
  margin-bottom: 20px;
}

.instructions {
  font-family: "Gilroy";
  font-size: 14px;
  color: #828282;
  line-height: 1.5;
}

.hours-form {
  background: #ffffff;
  border-radius: 8px;
  padding: 20px;
}

.day-row {
  display: flex;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid #f3f3f3;
}

.day-row:last-child {
  border-bottom: none;
}

.day-checkbox {
  display: flex;
  align-items: center;
  min-width: 150px;
}

.day-name {
  font-family: "Gilroy";
  font-weight: 600;
  font-size: 16px;
  color: #1b1b1b;
  margin-left: 10px;
}

.time-inputs {
  display: flex;
  gap: 20px;
  flex: 1;
}

.time-input-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.time-input-group label {
  font-family: "Gilroy";
  font-size: 14px;
  color: #828282;
  min-width: 50px;
}

.time-input {
  font-family: "Gilroy";
  font-size: 14px;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  min-width: 120px;
}

.closed-label {
  font-family: "Gilroy";
  font-size: 14px;
  color: #828282;
  font-style: italic;
}

.action-buttons {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

ion-button {
  --border-radius: 8px;
  height: 48px;
  font-family: "Gilroy";
  font-weight: 600;
  font-size: 16px;
  text-transform: none;
}

.error-message {
  margin-top: 20px;
  padding: 15px;
  background-color: #f8d7da;
  color: #721c24;
  border-radius: 8px;
  font-family: "Gilroy";
  font-size: 14px;
}

.success-message {
  margin-top: 20px;
  padding: 15px;
  background-color: #d4edda;
  color: #155724;
  border-radius: 8px;
  font-family: "Gilroy";
  font-size: 14px;
}

@media (max-width: 600px) {
  .day-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .time-inputs {
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }
}
</style>
